import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  INTERVIEW_LANGUAGES,
  JOB_LIMITS,
  STAGE_LABELS,
  STAGE_ORDER,
  recommendedStructure,
} from '@ai-interview/shared';
import type { InterviewStage, Job, JobStatus, Seniority, Voice } from '@ai-interview/shared';
import { api } from '../lib/api';
import { ErrorNotice } from './Status';

const SENIORITIES: Seniority[] = ['intern', 'junior', 'mid', 'senior', 'staff', 'principal'];

interface DraftQuestion {
  text: string;
  competency: string;
  stage: InterviewStage;
  mustAsk: boolean;
  /** Undefined while the hint field is closed; an empty string while it is open and blank. */
  followUpHint?: string;
}

const EMPTY_QUESTION: DraftQuestion = { text: '', competency: '', stage: 'technical', mustAsk: false };

function draftQuestions(job?: Job): DraftQuestion[] {
  if (!job) return [{ ...EMPTY_QUESTION }];
  return job.questions.map((q) => ({
    text: q.text,
    competency: q.competency,
    stage: q.stage ?? 'technical',
    mustAsk: q.mustAsk,
    followUpHint: q.followUpHint,
  }));
}

/**
 * How many questions each section has against what is suggested for the
 * seniority. Guidance only - a role can deliberately differ.
 */
function StructureSummary({ seniority, questions }: { seniority: Seniority; questions: DraftQuestion[] }) {
  const { counts, durationMinutes } = recommendedStructure(seniority);
  return (
    <div className="row small">
      {STAGE_ORDER.map((stage) => {
        const have = questions.filter((q) => q.text.trim() && q.stage === stage).length;
        const [min, max] = counts[stage];
        return (
          <span key={stage} className={`badge ${have >= min && have <= max ? 'positive' : ''}`}>
            {STAGE_LABELS[stage]} {have} / {min === max ? min : `${min}–${max}`}
          </span>
        );
      })}
      <span className="muted">Suggested length about {durationMinutes} minutes.</span>
    </div>
  );
}

/**
 * The voices on the ElevenLabs account, with a preview. "Default" leaves the
 * role on whatever voice is published on the agent in the dashboard.
 */
function VoicePicker({ value, onChange }: { value: string; onChange: (voiceId: string) => void }) {
  const [voices, setVoices] = useState<Voice[]>();
  const [failed, setFailed] = useState(false);
  const audio = useRef<HTMLAudioElement>();

  useEffect(() => {
    api.listVoices().then(
      (result) => setVoices(result.items),
      () => setFailed(true),
    );
    return () => audio.current?.pause();
  }, []);

  const selected = voices?.find((voice) => voice.id === value);

  function preview(url: string) {
    audio.current?.pause();
    audio.current = new Audio(url);
    void audio.current.play();
  }

  return (
    <div>
      <label htmlFor="voice">Interviewer voice</label>
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <select id="voice" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{voices || failed ? 'Default - the voice published on the agent' : 'Loading voices…'}</option>
          {/* A saved voice that is no longer on the account still shows, rather than silently reading as Default. */}
          {value && !selected ? <option value={value}>Saved voice ({value})</option> : null}
          {voices?.map((voice) => (
            <option key={voice.id} value={voice.id}>
              {voice.name}
              {voice.description ? ` - ${voice.description}` : ''}
            </option>
          ))}
        </select>
        {selected?.previewUrl ? (
          <button type="button" className="secondary" onClick={() => preview(selected.previewUrl!)}>
            Preview
          </button>
        ) : null}
      </div>
      {failed ? (
        <p className="small muted" style={{ margin: '0.35rem 0 0' }}>
          Could not load the voices from ElevenLabs, so only the default and any saved voice are listed.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Creates a role, edits one, or starts a new role from a copy of one.
 *
 * One form for all three, so they cannot drift apart. An edit starts from
 * exactly what was saved and sends every field the form shows; status is left
 * as it is.
 */
export function RoleForm({
  initial,
  copyFrom,
  onSaved,
  onCancel,
}: {
  /** The role being edited. */
  initial?: Job;
  /** A role to duplicate: the form opens pre-filled and saves a new role. */
  copyFrom?: Job;
  onSaved: (job: Job) => void;
  onCancel?: () => void;
}) {
  const source = initial ?? copyFrom;
  const formRef = useRef<HTMLFormElement>(null);
  const [title, setTitle] = useState(() =>
    copyFrom ? `${copyFrom.title} (copy)`.slice(0, JOB_LIMITS.titleLength[1]) : (initial?.title ?? ''),
  );
  const [department, setDepartment] = useState(source?.department ?? '');
  const [location, setLocation] = useState(source?.location ?? '');
  const [seniority, setSeniority] = useState<Seniority>(source?.seniority ?? 'mid');
  const [language, setLanguage] = useState(source?.language ?? 'en');
  const [voiceId, setVoiceId] = useState(source?.voiceId ?? '');
  const [description, setDescription] = useState(source?.description ?? '');
  const [competencies, setCompetencies] = useState(source?.competencies.join(', ') ?? '');
  const [durationMinutes, setDurationMinutes] = useState(
    source?.durationMinutes ?? recommendedStructure('mid').durationMinutes,
  );
  // Once the length has been set by hand - or saved before - seniority stops changing it.
  const [durationTouched, setDurationTouched] = useState(Boolean(source));
  const [questions, setQuestions] = useState<DraftQuestion[]>(() => draftQuestions(source));
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState<'save' | 'draft'>();

  function updateQuestion(index: number, patch: Partial<DraftQuestion>) {
    setQuestions((current) => current.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  }

  async function save(status?: JobStatus) {
    setBusy(status === 'draft' ? 'draft' : 'save');
    setError(undefined);
    try {
      const competencyList = competencies
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      const filled = questions.filter((q) => q.text.trim().length > 0);

      // The API enforces these too. Checking here names the problem in the
      // form's own words, before a round trip, for the limits the inputs'
      // attributes cannot express.
      if (competencyList.length > JOB_LIMITS.competencies) {
        throw new Error(
          `List at most ${JOB_LIMITS.competencies} competencies - this has ${competencyList.length}.`,
        );
      }
      const tooLong = competencyList.find((c) => c.length > JOB_LIMITS.competencyLength);
      if (tooLong) {
        throw new Error(
          `Keep each competency to ${JOB_LIMITS.competencyLength} characters - "${tooLong.slice(0, 40)}…" is longer.`,
        );
      }
      if (filled.length === 0) throw new Error('Add at least one question.');

      const fields = {
        title: title.trim(),
        seniority,
        language,
        description: description.trim(),
        competencies: competencyList,
        questions: filled.map((q) => ({
          text: q.text.trim(),
          // Falling back to the first competency keeps the scorecard grouped
          // even when someone skips the field.
          competency: q.competency.trim() || competencyList[0] || 'General',
          stage: q.stage,
          mustAsk: q.mustAsk,
          ...(q.followUpHint?.trim() ? { followUpHint: q.followUpHint.trim() } : {}),
        })),
        durationMinutes,
      };

      const saved = initial
        ? // Blank department, location and voice are sent so an edit can clear them.
          await api.updateJob(initial.id, {
            ...fields,
            department: department.trim(),
            location: location.trim(),
            voiceId,
          })
        : await api.createJob({
            ...fields,
            department: department.trim() || undefined,
            location: location.trim() || undefined,
            voiceId: voiceId || undefined,
            status: status ?? 'open',
          });
      onSaved(saved);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(undefined);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void save();
  }

  function saveDraft() {
    if (formRef.current?.reportValidity()) void save('draft');
  }

  return (
    <form ref={formRef} className="card stack" onSubmit={submit}>
      <h2>{initial ? 'Edit role' : copyFrom ? `Duplicate ${copyFrom.title}` : 'New role'}</h2>
      {initial ? (
        <p className="small muted" style={{ margin: 0 }}>
          Changes apply to interviews that start after you save, and to any re-score. Interviews
          already under way keep the questions they started with.
        </p>
      ) : null}
      {copyFrom ? (
        <p className="small muted" style={{ margin: 0 }}>
          A new role with the same questions and settings. Nothing about {copyFrom.title} or its
          candidates changes.
        </p>
      ) : null}
      <ErrorNotice error={error} />

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: '2 1 240px' }}>
          <label htmlFor="title">Title</label>
          <input
            id="title"
            required
            minLength={JOB_LIMITS.titleLength[0]}
            maxLength={JOB_LIMITS.titleLength[1]}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label htmlFor="seniority">Seniority</label>
          <select
            id="seniority"
            value={seniority}
            onChange={(e) => {
              const next = e.target.value as Seniority;
              setSeniority(next);
              if (!durationTouched) setDurationMinutes(recommendedStructure(next).durationMinutes);
            }}
          >
            {SENIORITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: '0 1 120px' }}>
          <label htmlFor="duration">Minutes</label>
          <input
            id="duration"
            type="number"
            min={5}
            max={60}
            value={durationMinutes}
            onChange={(e) => {
              setDurationTouched(true);
              setDurationMinutes(Number(e.target.value));
            }}
          />
        </div>
      </div>

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="department">Department</label>
          <input
            id="department"
            maxLength={JOB_LIMITS.departmentLength}
            placeholder="Engineering"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="location">Location</label>
          <input
            id="location"
            maxLength={JOB_LIMITS.locationLength}
            placeholder="Pune, hybrid"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
      </div>

      <div className="stack" style={{ gap: '0.5rem' }}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label htmlFor="language">Interview language</label>
            <select id="language" value={language} onChange={(e) => setLanguage(e.target.value)}>
              {INTERVIEW_LANGUAGES.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
              {INTERVIEW_LANGUAGES.some((option) => option.code === language) ? null : (
                <option value={language}>{language}</option>
              )}
            </select>
          </div>
          <div style={{ flex: '3 1 320px' }}>
            <VoicePicker value={voiceId} onChange={setVoiceId} />
          </div>
        </div>
        {language !== 'en' || voiceId ? (
          <p className="small muted" style={{ margin: 0 }}>
            The voice and speech model on the ElevenLabs agent must support the language. Take a short
            test interview before inviting candidates.
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor="description">Job description</label>
        <textarea
          id="description"
          required
          minLength={JOB_LIMITS.descriptionLength[0]}
          maxLength={JOB_LIMITS.descriptionLength[1]}
          placeholder="Paste the job description. The interviewer uses it to ground follow-up questions."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="competencies">Competencies (comma separated)</label>
        <input
          id="competencies"
          required
          placeholder="Backend engineering, System design, Ownership, Communication"
          value={competencies}
          onChange={(e) => setCompetencies(e.target.value)}
        />
        <p className="small muted">
          The scorecard reports one score per competency. Up to {JOB_LIMITS.competencies}.
        </p>
      </div>

      <div className="stack">
        <label>Questions</label>
        <p className="small muted" style={{ margin: 0 }}>
          Tag each question with its section. The interviewer always opens with a short warm-up and
          the candidate&apos;s background, then runs soft skills, situational and technical questions in
          that order - write soft skills as &ldquo;tell me about a time&rdquo; questions, and technical
          ones as reasoning or scenarios rather than anything that needs code written out loud.
        </p>
        <StructureSummary seniority={seniority} questions={questions} />
        {questions.map((question, index) => (
          <div key={index} className="stack" style={{ gap: '0.4rem' }}>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: '3 1 320px' }}>
                <input
                  aria-label={`Question ${index + 1}`}
                  placeholder="Tell me about a system you designed end to end."
                  minLength={JOB_LIMITS.questionLength[0]}
                  maxLength={JOB_LIMITS.questionLength[1]}
                  value={question.text}
                  onChange={(e) => updateQuestion(index, { text: e.target.value })}
                />
              </div>
              <div style={{ flex: '0 1 150px' }}>
                <select
                  aria-label={`Question ${index + 1} section`}
                  value={question.stage}
                  onChange={(e) => updateQuestion(index, { stage: e.target.value as InterviewStage })}
                >
                  {STAGE_ORDER.map((stage) => (
                    <option key={stage} value={stage}>
                      {STAGE_LABELS[stage]}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <input
                  aria-label={`Question ${index + 1} competency`}
                  placeholder="Competency"
                  maxLength={JOB_LIMITS.competencyLength}
                  value={question.competency}
                  onChange={(e) => updateQuestion(index, { competency: e.target.value })}
                />
              </div>
              <label className="row small" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={question.mustAsk}
                  onChange={(e) => updateQuestion(index, { mustAsk: e.target.checked })}
                />
                Must ask
              </label>
              {question.followUpHint === undefined ? (
                <button type="button" className="secondary" onClick={() => updateQuestion(index, { followUpHint: '' })}>
                  Add hint
                </button>
              ) : null}
              <button
                type="button"
                className="danger"
                onClick={() => setQuestions((current) => current.filter((_, i) => i !== index))}
                disabled={questions.length === 1}
              >
                Remove
              </button>
            </div>
            {question.followUpHint === undefined ? null : (
              <div className="row" style={{ paddingLeft: '1rem' }}>
                <input
                  aria-label={`Question ${index + 1} follow-up hint`}
                  style={{ flex: '1 1 320px' }}
                  placeholder="What a strong answer covers - the interviewer probes for it if the candidate does not."
                  maxLength={JOB_LIMITS.followUpHintLength}
                  value={question.followUpHint}
                  onChange={(e) => updateQuestion(index, { followUpHint: e.target.value })}
                />
                <button
                  type="button"
                  className="secondary"
                  onClick={() => updateQuestion(index, { followUpHint: undefined })}
                >
                  Remove hint
                </button>
              </div>
            )}
          </div>
        ))}
        <div className="row">
          <button
            type="button"
            className="secondary"
            onClick={() => setQuestions((current) => [...current, { ...EMPTY_QUESTION }])}
            disabled={questions.length >= JOB_LIMITS.questions}
          >
            Add question
          </button>
          {questions.length >= JOB_LIMITS.questions ? (
            <span className="small muted">A role can have up to {JOB_LIMITS.questions} questions.</span>
          ) : null}
        </div>
      </div>

      <div className="row">
        <button type="submit" disabled={busy !== undefined}>
          {initial
            ? busy
              ? 'Saving…'
              : 'Save changes'
            : busy === 'save'
              ? 'Creating…'
              : 'Create role'}
        </button>
        {initial ? null : (
          <button type="button" className="secondary" onClick={saveDraft} disabled={busy !== undefined}>
            {busy === 'draft' ? 'Saving…' : 'Save as draft'}
          </button>
        )}
        {onCancel ? (
          <button type="button" className="secondary" onClick={onCancel} disabled={busy !== undefined}>
            Cancel
          </button>
        ) : null}
      </div>
      {initial ? null : (
        <p className="small muted" style={{ margin: 0 }}>
          Create role opens it for invites straight away. A draft cannot be used for invites until you
          open it.
        </p>
      )}
    </form>
  );
}
